import React from "react";
import "./SceneCard.css";
import {Accordion, ListGroup} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLayerGroup, faFilm, faCode, faObjectGroup} from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import {tagColor, deviceTypeBadgeColor, getBondColor} from "../../../../../../lib/ui/colorUtils";
import {padDeviceId} from "../../../../../../lib/utils";
import ReactJson from "react-json-view";
import { useTimezone } from "../../../../contexts/TimezoneContext";

function hsvToHex(h, s, v) {
    const sN = (Number(s) || 0) / 100;
    const vN = (Number(v) || 0) / 100;
    const hN = ((Number(h) || 0) % 360 + 360) % 360;
    const c = vN * sN;
    const x = c * (1 - Math.abs(((hN / 60) % 2) - 1));
    const m = vN - c;
    let r = 0, g = 0, b = 0;
    if (hN < 60)       { r = c; g = x; b = 0; }
    else if (hN < 120) { r = x; g = c; b = 0; }
    else if (hN < 180) { r = 0; g = c; b = x; }
    else if (hN < 240) { r = 0; g = x; b = c; }
    else if (hN < 300) { r = x; g = 0; b = c; }
    else               { r = c; g = 0; b = x; }
    const to2 = n => Math.round((n + m) * 255).toString(16).padStart(2, '0');
    return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function renderActorArgument(argument) {
    if (typeof argument !== 'object' || argument === null) return argument;
    if ('h' in argument && 's' in argument && 'v' in argument) {
        const {h, s, v} = argument;
        return (
            <span className="d-inline-flex align-items-center" style={{gap: '4px'}}>
                <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.3)',
                    backgroundColor: hsvToHex(h, s, v),
                }}/>
                <span>{`H${h} S${s} V${v}`}</span>
            </span>
        );
    }
    return JSON.stringify(argument);
}

function SceneCard({scenes = [], devices = [], groups = []}) {
    const { formatDate, getRelativeTime } = useTimezone();

    function getDeviceInfo(deviceId) {
        const paddedSearchId = padDeviceId(deviceId);
        const device = devices.find(d => {
            const dId = d.device_id || d.id;
            const paddedDId = padDeviceId(dId);
            return dId === deviceId || dId === paddedSearchId ||
                   paddedDId === deviceId || paddedDId === paddedSearchId ||
                   d.id.endsWith(`:${deviceId}`) || d.id.endsWith(`:${paddedSearchId}`);
        });
        return device;
    }

    function getGroupInfo(groupId) {
        if (!groupId) return null;
        return groups.find(g => (g.group_id || g.id) === groupId) || null;
    }

    function renderGroupProperties(group) {
        const infoKeys = ["state", "properties", "actions"];
        return (<Accordion className={"mt-2"}>
            {infoKeys.map((key, index) => group[key] ? (<Accordion.Item eventKey={index.toString()} key={key}>
                <Accordion.Header>{key.charAt(0).toUpperCase() + key.slice(1)}</Accordion.Header>
                <Accordion.Body className={"p-3"}>
                    {typeof group[key] === "object" && !Array.isArray(group[key]) ? (Object.entries(group[key]).sort(([a], [b]) => {
                        if (a.startsWith("_") && !b.startsWith("_")) return 1;
                        if (!a.startsWith("_") && b.startsWith("_")) return -1;
                        return a.localeCompare(b);
                    }).map(([subKey, subValue]) => (
                        <FloatingLabel label={subKey.charAt(0).toUpperCase() + subKey.slice(1)}
                                       key={subKey}>
                            <Form.Control readOnly={true} value={subValue ?? ""}/>
                        </FloatingLabel>))) : (<FloatingLabel label={key.charAt(0).toUpperCase() + key.slice(1)}>
                        <Form.Control readOnly={true} value={group[key] ?? ""}/>
                    </FloatingLabel>)}
                </Accordion.Body>
            </Accordion.Item>) : null)}
        </Accordion>);
    }


    if (!scenes || scenes.length === 0) {
        return null;
    }

    return (<Accordion defaultActiveKey="0" className="mb-2">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div>
                                <FontAwesomeIcon icon={faFilm} className="me-2"/>
                                Scenes
                            </div>
                            <Badge bg="dark">
                                {scenes.length}
                            </Badge>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-2">
                        <Accordion alwaysOpen>
                            {scenes.map((scene, index) => {
                                const sceneId = scene.id || scene.scene_id || index;
                                return <Accordion.Item eventKey={`${sceneId}-scene`}
                                                       key={`${sceneId}-scene`}>
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center w-100" style={{gap: '0.5rem'}}>
                                            <Badge bg={"success"} className="flex-shrink-0">
                                                <FontAwesomeIcon icon={faFilm} className={"me-1"}/> Scene
                                            </Badge>
                                            <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                {scene.name || `Scene ${index + 1}`}
                                            </span>
                                            <div className="d-flex flex-shrink-0" style={{gap: '0.25rem'}}>
                                                {scene.bond_ids && scene.bond_ids.length > 0 ? (
                                                    scene.bond_ids.map((bondId, idx) => (
                                                        <Badge key={idx} className="bondBadge" style={{'--bond-badge-bg': getBondColor(bondId)}}>{bondId}
                                                        </Badge>
                                                    ))
                                                ) : scene.bond_id && (
                                                    <Badge className="bondBadge" style={{'--bond-badge-bg': getBondColor(scene.bond_id)}}>{scene.bond_id}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </Accordion.Header>

                                    <Accordion.Body className={"p-3"}>
                                        <FloatingLabel label="Name" className="mb-2">
                                            <Form.Control readOnly={true} value={scene.name || ""}/>
                                        </FloatingLabel>

                                        {(scene.scene_id || scene.id) && (
                                            <FloatingLabel label="Scene ID" className="mb-2">
                                                <Form.Control readOnly={true} value={scene.scene_id || scene.id}/>
                                            </FloatingLabel>
                                        )}

                                        {scene.hash && (
                                            <FloatingLabel label="Hash" className="mb-2">
                                                <Form.Control readOnly={true} value={scene.hash}/>
                                            </FloatingLabel>
                                        )}

                                        <div className="d-flex gap-2 mb-2">
                                            {scene.created && (
                                                <FloatingLabel label={`Created ${getRelativeTime(scene.created)}`} className="flex-fill">
                                                    <Form.Control readOnly={true} value={formatDate(scene.created)}/>
                                                </FloatingLabel>
                                            )}
                                            {scene.modified && (
                                                <FloatingLabel label={`Modified ${getRelativeTime(scene.modified)}`} className="flex-fill">
                                                    <Form.Control readOnly={true} value={formatDate(scene.modified)}/>
                                                </FloatingLabel>
                                            )}
                                        </div>

                                        {/* Bond IDs Display */}
                                        {scene.bond_ids && scene.bond_ids.length > 0 && (
                                            <div className="mb-3">
                                                <small className="text-muted fw-bold">Bridges:</small>
                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                    {scene.bond_ids.map((bondId, idx) => (
                                                        <Badge key={idx} className="bondBadge" style={{'--bond-badge-bg': getBondColor(bondId)}}>
                                                            {bondId}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Types Display */}
                                        {scene.types && scene.types.length > 0 && (
                                            <div className="mb-3">
                                                <small className="text-muted fw-bold">Types:</small>
                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                    {scene.types.map((type, idx) => (
                                                        <Badge key={idx} bg="primary">
                                                            {type}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Locations Display */}
                                        {scene.locations && scene.locations.length > 0 && (
                                            <div className="mb-3">
                                                <small className="text-muted fw-bold">Locations:</small>
                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                    {scene.locations.map((location, idx) => (
                                                        <Badge key={idx} bg="success">
                                                            {location}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Actors Display */}
                                        {scene.actors && scene.actors.length > 0 && (
                                            <div className="mb-3">
                                                <h6>Actors</h6>
                                                <ListGroup>
                                                    {scene.actors.map((actor, idx) => {
                                                        const isGroup  = !!actor.group;
                                                        const group    = isGroup ? getGroupInfo(actor.group) : null;
                                                        const device   = !isGroup ? getDeviceInfo(actor.device) : null;
                                                        const bondId   = device?.bond_id || group?.bond_id || null;
                                                        const argBadge = actor.argument != null ? (
                                                            <Badge bg="secondary" className="flex-shrink-0" style={{fontFamily: 'monospace'}}>
                                                                {renderActorArgument(actor.argument)}
                                                            </Badge>
                                                        ) : null;

                                                        // Unknown actor (no device or group resolved)
                                                        if (!device && !group) {
                                                            return (
                                                                <ListGroup.Item key={idx}>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {isGroup
                                                                            ? <Badge bg="secondary" className="flex-shrink-0"><FontAwesomeIcon icon={faObjectGroup} className="me-1" />Group</Badge>
                                                                            : <Badge bg="info" className="flex-shrink-0">Device</Badge>
                                                                        }
                                                                        <Badge bg="dark" className="flex-shrink-0" style={{fontFamily: 'monospace'}}>
                                                                            {actor.group || actor.device}
                                                                        </Badge>
                                                                        <Badge bg="primary" className="flex-shrink-0">{actor.action || "Action"}</Badge>
                                                                        {argBadge}
                                                                    </div>
                                                                </ListGroup.Item>
                                                            );
                                                        }

                                                        // Group actor
                                                        if (isGroup && group) {
                                                            return (
                                                                <ListGroup.Item key={idx}>
                                                                    <div className="d-flex align-items-center w-100" style={{gap: '0.5rem', minWidth: 0}}>
                                                                        <Badge bg="primary" className="flex-shrink-0">
                                                                            {actor.action || "Action"}
                                                                        </Badge>
                                                                        <Badge bg="secondary" className="flex-shrink-0">
                                                                            <FontAwesomeIcon icon={faObjectGroup} className="me-1" />Group
                                                                        </Badge>
                                                                        <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                                            {group.name || actor.group}
                                                                        </span>
                                                                        {argBadge}
                                                                        {bondId && (
                                                                            <Badge className="bondBadge flex-shrink-0" style={{'--bond-badge-bg': getBondColor(bondId)}}>{bondId}</Badge>
                                                                        )}
                                                                    </div>
                                                                </ListGroup.Item>
                                                            );
                                                        }

                                                        // Device actor
                                                        return (
                                                            <ListGroup.Item key={idx}>
                                                                <div className="d-flex align-items-center w-100" style={{gap: '0.5rem', minWidth: 0}}>
                                                                    <Badge bg="primary" className="flex-shrink-0">
                                                                        {actor.action || "Action"}
                                                                    </Badge>
                                                                    <Badge className="flex-shrink-0"
                                                                           style={deviceTypeBadgeColor(device.device_type || device.type)}>
                                                                        {device.device_type || device.type || "Device"}
                                                                    </Badge>
                                                                    <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                                        {device.name}
                                                                    </span>
                                                                    {argBadge}
                                                                    <div className="d-flex flex-shrink-0" style={{gap: '0.25rem'}}>
                                                                        {(device.properties?.branding_profile || device.properties?.pairing_assets_key) && (
                                                                            <Badge bg={"dark"}>
                                                                                {device.properties.branding_profile || device.properties.pairing_assets_key}
                                                                            </Badge>
                                                                        )}
                                                                        {bondId && (
                                                                            <Badge className="bondBadge" style={{'--bond-badge-bg': getBondColor(bondId)}}>{bondId}</Badge>
                                                                        )}
                                                                        {device.template_id ? (
                                                                            <Badge bg={"info"} style={tagColor("template", device.template_id)}>{device.template_id}</Badge>
                                                                        ) : device.template ? (
                                                                            <Badge bg={"info"} style={tagColor("template", device.template)}>{device.template}</Badge>
                                                                        ) : (
                                                                            <Badge bg={"warning"}>RAW</Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        );
                                                    })}
                                                </ListGroup>
                                            </div>
                                        )}

                                        {/* Devices Display */}
                                        {scene.devices && (
                                            <div className="mb-3">
                                                <small className="text-muted fw-bold">Devices:</small>
                                                <div className="d-flex flex-wrap gap-1 mt-1">
                                                    {(Array.isArray(scene.devices) ? scene.devices : [scene.devices]).map((deviceId, idx) => (
                                                        <Badge key={idx} bg="info">
                                                            {deviceId}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Commands Display */}
                                        {scene.commands && Object.keys(scene.commands).length > 0 && (
                                            <div className="mb-3">
                                                <h6>Commands</h6>
                                                <ListGroup>
                                                    {Object.entries(scene.commands).map(([deviceId, command], idx) => (
                                                        <ListGroup.Item key={idx}>
                                                            <Badge bg="info" className="me-2">{deviceId}</Badge>
                                                            <small className="text-muted">
                                                                {typeof command === 'object' ? JSON.stringify(command) : command}
                                                            </small>
                                                        </ListGroup.Item>
                                                    ))}
                                                </ListGroup>
                                            </div>
                                        )}

                                        {renderGroupProperties(scene)}

                                        {/* JSON Viewer Section */}
                                        <div className="mt-3">
                                            <Accordion>
                                                <Accordion.Item eventKey="json">
                                                    <Accordion.Header>
                                                        <FontAwesomeIcon icon={faCode} className="me-2"/>
                                                        Raw JSON
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <ReactJson
                                                            src={scene.raw_scenes || [scene]}
                                                            theme="ocean"
                                                            collapsed={1}
                                                            displayDataTypes={false}
                                                            displayObjectSize={false}
                                                            enableClipboard={true}
                                                            name={false}
                                                        />
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </div>
                                    </Accordion.Body>

                                </Accordion.Item>
                            })}
                        </Accordion>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>);
}

export default SceneCard;
